import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  companiesApi,
  type CompanyUsage,
  type UpdateCompanyInput,
} from '@/features/companies/companies.api';
import { useNotification } from '@/contexts/NotificationContext';
import { getErrorMessage } from '@/utils/errorHandling';
import { bytesToGb, gbToBytes } from '@/utils/bytes';

const schema = z.object({
  legalName: z.string().min(1, 'Razão social é obrigatória'),
  customerLimit: z.coerce.number().int().positive('Deve ser maior que zero'),
  aiTokenLimit: z.coerce.number().int().positive('Deve ser maior que zero'),
  storageLimitGb: z.coerce.number().positive('Deve ser maior que zero'),
});

type FormData = z.infer<typeof schema>;

interface EditCompanyDialogProps {
  open: boolean;
  company: CompanyUsage | null;
  onClose: () => void;
}

export const EditCompanyDialog: React.FC<EditCompanyDialogProps> = ({ open, company, onClose }) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      legalName: '',
      customerLimit: 0,
      aiTokenLimit: 0,
      storageLimitGb: 0,
    },
  });

  useEffect(() => {
    if (company) {
      reset({
        legalName: company.legalName,
        customerLimit: company.customerLimit,
        aiTokenLimit: company.aiTokenLimit,
        storageLimitGb: bytesToGb(company.storageLimit),
      });
    }
  }, [company, reset]);

  const updateMutation = useMutation({
    mutationFn: (input: UpdateCompanyInput) => companiesApi.update(company!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'companies'] });
      showNotification('Empresa atualizada com sucesso', 'success');
      onClose();
    },
    onError: err => {
      showNotification(getErrorMessage(err, 'Erro ao atualizar empresa'), 'error');
    },
  });

  const handleClose = () => {
    if (!updateMutation.isPending) {
      onClose();
    }
  };

  const onSubmit = (data: FormData) => {
    if (!company) return;
    updateMutation.mutate({
      legalName: data.legalName,
      customerLimit: data.customerLimit,
      aiTokenLimit: data.aiTokenLimit,
      storageLimit: gbToBytes(data.storageLimitGb),
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Editar empresa
          </Typography>
          {company && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              CNPJ: {company.cnpj}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={updateMutation.isPending}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Controller
              name="legalName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Razão social"
                  error={!!errors.legalName}
                  helperText={errors.legalName?.message}
                  disabled={updateMutation.isPending}
                />
              )}
            />

            <Typography variant="subtitle2" sx={{ pt: 1 }}>
              Limites
            </Typography>

            <Controller
              name="customerLimit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label="Limite de clientes"
                  error={!!errors.customerLimit}
                  helperText={errors.customerLimit?.message}
                  disabled={updateMutation.isPending}
                />
              )}
            />

            <Controller
              name="aiTokenLimit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label="Limite de tokens de IA"
                  error={!!errors.aiTokenLimit}
                  helperText={errors.aiTokenLimit?.message}
                  disabled={updateMutation.isPending}
                />
              )}
            />

            <Controller
              name="storageLimitGb"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label="Limite de storage (GB)"
                  error={!!errors.storageLimitGb}
                  helperText={errors.storageLimitGb?.message}
                  disabled={updateMutation.isPending}
                  slotProps={{ htmlInput: { step: '0.1', min: '0' } }}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined" disabled={updateMutation.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isPending}
            startIcon={updateMutation.isPending ? <CircularProgress size={18} /> : null}
          >
            {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
